import { I18n } from '@calendly/i18n';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { IconButton } from '@calendly/ui/components/icon-button';
import { SettingsMenuVerticalIcon, ExportIcon } from '@calendly/ui/components/icons';
import { Menu, BareItem } from '@calendly/ui/components/menu';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextActionContrast } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ExportPopup from 'components/export/confirmation';
import { confirmationOpenSelector, closeConfirmation, createDelayedExport } from 'components/export/event_invitees/slice';
import { BOOKING_METHODS } from 'features/one_off_modal';
import { useTrack } from 'hooks/analytics_events';
import { isCalendlyMeeting } from 'shared/utils';
import { CalendlyInviteesContent } from './calendly_invitees_content';
import { EditInviteeLimitButton } from './edit_invitee_limit_button';
import { ExternalEvent } from './external_event';
import { ItemContents } from './item_contents';
import { WidgetContainer } from '../widget_container';
const headerContainerIsGroupCss = "h1v0f10h";
const headerContainerCss = "h1lbxen5";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    isGroup,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, {
    [headerContainerIsGroupCss]: isGroup
  }, props.className)} />;
});
const styledMenuCss = "s1o08wp6";
const StyledMenu = forwardRef((props, ref) => <Menu ref={ref} {...props} className={classNames(styledMenuCss, props.className)} />);
const styledGroupCss = "s1cht9zy";
const StyledGroup = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(styledGroupCss, props.className)} />;
});
const exportButtonContainerCss = "ejg0quy";
const ExportButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(exportButtonContainerCss, props.className)} />;
});
const exportButtonIconCss = "e7vagbd";
const ExportButton = ({
  event,
  isInMenu
}) => {
  const dispatch = useDispatch();
  const confirmationOpen = useSelector(confirmationOpenSelector);
  const track = useTrack();
  const useStandardIconColor = event?.eventType?.kind !== 'group';
  const handleClick = async () => {
    track({
      name: 'Meeting Interaction',
      properties: {
        action: 'export_attendees',
        location: 'meetings_page_right_rail',
        event_uuid: event?.uuid,
        mi_event_uuid: event?.meetingIntelligence?.uuid,
        is_calendly_meeting: isCalendlyMeeting(event)
      }
    });
    await dispatch(createDelayedExport({
      eventId: event.id
    }));
  };
  if (isInMenu) {
    return <>
        <BareItem onClick={handleClick}>
          <ItemContents>
            <ExportIcon className={useStandardIconColor ? exportButtonIconCss : undefined} />
            <BodyText>{I18n.t('publisher.meetings.invitees.export_attendees')}</BodyText>
          </ItemContents>
        </BareItem>
        {confirmationOpen && <ExportPopup onConfirm={() => dispatch(closeConfirmation())} />}
      </>;
  }
  return <>
      <MinimalButton className={useStandardIconColor ? exportButtonIconCss : undefined} onClick={handleClick} title={I18n.t('publisher.meetings.invitees.export_attendees')} aria-label={I18n.t('publisher.meetings.invitees.export_attendees')}>
        <ExportIcon />
        {!useStandardIconColor && <BodyText color={colorTextActionContrast}>
            {I18n.t('publisher.meetings.invitees.export_attendees')}
          </BodyText>}
      </MinimalButton>
      {confirmationOpen && <ExportPopup onConfirm={() => dispatch(closeConfirmation())} />}
    </>;
};
const Header = ({
  event
}) => {
  const isGroup = event.eventType?.kind === 'group';
  const isPast = event.past;
  const isPoll = event.eventType?.bookingMethod === BOOKING_METHODS.poll;
  const [toggle, Dropdown, isExpanded] = useDropdown();
  const shouldShowEditInviteeLimit = isGroup && event.policy?.update && !isPoll && !isPast;
  const isCalendly = isCalendlyMeeting(event);
  const getSubtitle = () => {
    if (!isCalendly) return null;
    if (!isGroup) return null;
    const translation = I18n.t('publisher.meetings.invitees.spots_filled', {
      count: event.inviteesLimit,
      filledCount: event.filledCount
    });
    return typeof translation === 'string' ? translation : String(translation);
  };
  const renderControls = () => {
    if (!isCalendly) return null;
    if (shouldShowEditInviteeLimit) {
      return <>
          <IconButton size="small" onClick={toggle} title={I18n.t('publisher.meetings.invitees.invitees_actions')} aria-label={I18n.t('publisher.meetings.invitees.invitees_actions')} aria-expanded={isExpanded}>
            <SettingsMenuVerticalIcon />
          </IconButton>
          <Dropdown>
            <StyledMenu>
              <StyledGroup>
                <EditInviteeLimitButton event={event} />
                <ExportButton event={event} isInMenu />
              </StyledGroup>
            </StyledMenu>
          </Dropdown>
        </>;
    }
    return <ExportButtonContainer>
        <ExportButton event={event} isInMenu={false} />
      </ExportButtonContainer>;
  };
  const subtitle = getSubtitle();
  return <HeaderContainer isGroup={isGroup}>
      {subtitle && <BodyText color="muted" size="small">{subtitle}</BodyText>}
      {renderControls()}
    </HeaderContainer>;
};

// Single tree for both solo and group: same component and hooks to avoid React render error
// when switching event types. CalendlyInviteesContent uses BaseAttendeeCard for every row.
const CalendlyEvent = ({
  event,
  readonly
}) => <CalendlyInviteesContent key={event.id} event={event} readonly={readonly} />;
export const InviteesWidget = ({
  event,
  readonly
}) => {
  const title = I18n.t('publisher.meetings.invitees.invitees');

  // Don't render if there are no invitees/attendees. This only applies to
  // ExternalEvents (Calendly events always have at least one invitee)
  const shouldHideInviteesWidget = !isCalendlyMeeting(event) && (event?.invitees?.length || 0) === 0 && (event?.eventMemberships?.length || 0) === 0;
  if (shouldHideInviteesWidget) {
    return null;
  }
  return <WidgetContainer title={title} header={<Header event={event} />} noPadding showBorder={false} sectionWithBottomBorder>
      {isCalendlyMeeting(event) ? <CalendlyEvent event={event} readonly={readonly} /> : <ExternalEvent event={event} readonly={readonly} />}
    </WidgetContainer>;
};

require("./invitees_widget.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./invitees_widget.js");